import { useNavigate } from '@tanstack/react-router';
import { isToday } from 'date-fns';

import { EventBullet } from '@/components/calendar/components/month-view/event-bullet';
import { useCalendar } from '@/components/calendar/contexts/calendar-context';
import { getCalendarEventColor } from '@/components/calendar/event-colors';

import { cn } from '@/lib/utils';

import type { IEvent } from '@/components/calendar/interfaces';

interface IProps {
  day: number;
  date: Date;
  events: IEvent[];
}

export function YearViewDayCell({ day, date, events }: IProps) {
  const navigate = useNavigate();
  const { setSelectedDate, config, colorBy } = useCalendar();
  const routerBase = config.routerBasePath ?? '/dashboard/content-calendar';

  const colorFor = (event: IEvent) =>
    config.colorByEnabled ? getCalendarEventColor(event, colorBy) : event.color;
  const maxIndicators = 3;
  const eventCount = events.length;

  const handleClick = () => {
    setSelectedDate(date);
    void navigate({ to: `${routerBase}/day` as never });
  };

  return (
    <button
      onClick={handleClick}
      type="button"
      className="flex h-11 flex-1 flex-col items-center justify-start gap-0.5 rounded-md pt-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <div
        className={cn(
          'flex size-6 items-center justify-center rounded-full text-xs font-medium',
          isToday(date) && 'bg-primary font-semibold text-primary-foreground'
        )}
      >
        {day}
      </div>

      {eventCount > 0 && (
        <div className="mt-0.5 flex gap-0.5">
          {eventCount <= maxIndicators ? (
            events.map((event) => (
              <EventBullet
                key={event.id}
                color={colorFor(event)}
                className="size-1.5"
              />
            ))
          ) : (
            <>
              <EventBullet color={colorFor(events[0])} className="size-1.5" />
              <span className="text-[7px] text-muted-foreground">
                +{eventCount - 1}
              </span>
            </>
          )}
        </div>
      )}
    </button>
  );
}
